import {RunTag} from '@wandb/weave/generated/graphql';

import {Run} from '../../util/runTypes';
import {RunSelectorRunsData} from './actions';

/**
 * Deletes a run by name from an arbitrary runs array. If the run is not found
 * in the array, the original array is returned.
 *
 * @param runs
 * @param runNameToDelete
 * @return The runs array with the deleted run removed, if any.
 */
export function deleteRun(runs: Run[], runNameToDelete: string): Run[] {
  if (!runs.find(run => run.name === runNameToDelete)) {
    return runs;
  }
  return runs.filter(run => run.name !== runNameToDelete);
}

/**
 * Updates a run by name in an arbitrary runs array. If the run is not found
 * in the array, the original array is returned.
 *
 * @param runs
 * @param runNameToUpdate
 * @param vars
 * @return The runs array with the updated run, if any.
 */
export function updateRun(
  runs: Run[],
  runNameToUpdate: string,
  vars: Partial<Run>
): Run[] {
  if (!runs.find(run => run.name === runNameToUpdate)) {
    return runs;
  }

  return runs.map(run => {
    if (run.name === runNameToUpdate) {
      return {
        ...run,
        ...vars,
      };
    }
    return run;
  });
}

/**
 * Updates tags for multiple runs in an arbitrary runs array. Each entry in the
 * tagMap corresponds to a run name and the tags to set for that run.
 *
 * @param runs
 * @param tagMap
 * @return The runs array with the runs with updated tags.
 */
export function updateRunTags(runs: Run[], tagMap: Record<string, RunTag[]>) {
  return Object.entries(tagMap).reduce((updatedRuns, [runName, tags]) => {
    return updateRun(updatedRuns, runName, {tags});
  }, runs);
}

type RunSelectorRuns = Record<string, RunSelectorRunsData>;

/**
 * Applies a function to the runs and pinnedRuns arrays for each run query
 * in the provided Redux state. Eliminates a ton of boilerplate for modifying
 * all runs in all queries optimistically.
 *
 * @param state
 * @param fn
 */
export function updateRuns(
  state: RunSelectorRuns,
  fn: (runs: Run[]) => Run[]
): RunSelectorRuns {
  const newState: RunSelectorRuns = {...state};
  for (const queryId of Object.keys(state)) {
    const {runs, pinnedRuns} = state[queryId];

    newState[queryId] = {
      ...state[queryId],
      runs: fn(runs),
      pinnedRuns: fn(pinnedRuns),
    };
  }
  return newState;
}
